import React from 'react';

import { Box, Divider, Skeleton, Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';

import { iconColorPairs } from '../../InvestorServicerSummary.constants';
import { fieldPropShape, iconColorPropType } from '../../InvestorServicerSummary.types';

import {
  cardWrapperStyles,
  dividerStyles,
  fieldRowStyles,
  fieldsStyles,
  fieldValueStyles,
  headerStyles,
  headerTextStyles,
  iconWrapperStyles,
} from './EntityCard.styles';

const EMPTY_FIELDS = [];

const EntityCard = ({
  isLoading,
  title,
  name,
  icon,
  iconColor = 'primary',
  isModified = false,
  authors = null,
  fields = EMPTY_FIELDS,
}) => {
  const iconStyles = iconWrapperStyles(iconColorPairs[iconColor]);

  return (
    <Box sx={cardWrapperStyles}>
      <Box sx={headerStyles}>
        <Box sx={iconStyles}>{icon}</Box>
        <Box sx={headerTextStyles}>
          <Typography color="text.secondary" variant="regular14">
            {title}
          </Typography>
          {isLoading ? (
            <Skeleton height={28} variant="text" width="70%" />
          ) : (
            <PendingChangeWrapper
              isSmall
              authors={authors}
              fieldLabel={title}
              pendingChangesActionType={isModified ? PendingChangesActionType.MODIFIED : null}
              textSelector="span"
            >
              <Typography data-truncated className="truncate" variant="bold20">
                {name}
              </Typography>
            </PendingChangeWrapper>
          )}
        </Box>
      </Box>

      <Divider sx={dividerStyles} />

      <Box sx={fieldsStyles}>
        {fields.map(
          ({
            id,
            name: fieldName,
            value,
            isModified: fieldIsModified,
            authors: fieldAuthors,
            valueNode,
          }) => (
            <Box key={id} sx={fieldRowStyles}>
              <Typography color="text.secondary" variant="regular14">
                {fieldName}
              </Typography>
              {isLoading ? (
                <Skeleton height={20} variant="text" width="40%" />
              ) : (
                <Box sx={fieldValueStyles}>
                  <PendingChangeWrapper
                    isSmall
                    authors={fieldAuthors}
                    fieldLabel={fieldName}
                    pendingChangesActionType={
                      fieldIsModified ? PendingChangesActionType.MODIFIED : null
                    }
                    textSelector="span"
                  >
                    {valueNode || (
                      <Typography data-truncated className="truncate" variant="medium14">
                        {value}
                      </Typography>
                    )}
                  </PendingChangeWrapper>
                </Box>
              )}
            </Box>
          ),
        )}
      </Box>
    </Box>
  );
};

EntityCard.propTypes = {
  ...WithLoadingPropTypes,
  title: PropTypes.string.isRequired,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.node.isRequired,
  iconColor: iconColorPropType,
  isModified: PropTypes.bool,
  authors: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(fieldPropShape),
};

export default EntityCard;
